import { formatUnits } from 'viem';

const _roundDecimal = (type: 'floor' | 'round' | 'ceil', number: number, decimals: number = 0) => {
  return Math[type](number * 10 ** decimals) / 10 ** decimals;
};

export const floorDecimals = (number: number, decimals: number = 0) => {
  return _roundDecimal('floor', number, decimals);
};

const SI_SYMBOL = ['', 'k', 'M', 'B', 'T', 'P', 'E'];

const TIME_UNITS = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30.42 * 24 * 60 * 60], // average month length
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
] as const;

export const abbreviateNumber = (
  number: number,
  decimals: number = 0,
  isInteger: boolean = false,
) => {
  // return the small numbers directly
  if (number < 1000) {
    if (isInteger) return number.toFixed(0);
    return number.toFixed(decimals);
  }

  // what tier? (determines SI symbol)
  const tier = (Math.log10(Math.abs(number)) / 3) | 0;

  // if zero, we don't need a suffix
  if (tier === 0) return number.toFixed(decimals);

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = number / scale;

  // Too large, larger than 10e+30 - use scientific notation
  if (!suffix) {
    return number.toExponential(decimals);
  }

  // format number and add suffix
  return scaled.toFixed(decimals) + suffix;
};

/**
 * Checks if the amount is "null" (zero).
 *
 * @param numeric - The amount string to check.
 */
export const isNullAmount = (numeric: number): boolean =>
  numeric <= 0 || Number.isNaN(numeric) || !Number.isFinite(numeric);

/**
 * Converts a base-unit token amount to its USD value.
 */
export const amountToUsd = (amount: string, decimals: number, usdPrice: number): number =>
  Number(formatUnits(BigInt(amount), decimals)) * usdPrice;

export const formatTimeElapsed = (timestamp: number, compact?: boolean): string => {
  const elapsedSeconds = Math.max(1, Math.floor(Date.now() / 1000) - timestamp);
  const [unit, duration] =
    TIME_UNITS.find(([, unitSeconds]) => elapsedSeconds >= unitSeconds) ??
    TIME_UNITS[TIME_UNITS.length - 1];

  const value = Math.floor(elapsedSeconds / duration);
  const separator = compact ? '' : ' ';

  const compactUnit = unit === 'month' ? 'mo' : unit.charAt(0);
  const formattedUnit = compact ? compactUnit : value === 1 ? unit : `${unit}s`;

  return `${value}${separator}${formattedUnit}`;
};

export const smallestTimestamp = (
  timestamps: (number | string | undefined)[],
): number | undefined => {
  if (!timestamps.length) return undefined;

  const numericTimestamps = timestamps
    .map((timestamp) => (timestamp ? Number(timestamp) : NaN))
    .filter((timestamp) => !isNullAmount(timestamp));

  return numericTimestamps.length > 0 ? Math.min(...numericTimestamps) : undefined;
};

export const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(value, max));
};
